import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCreditAccount,
  setCreditPlan,
  spendDemoDockCredit,
} from '../Models/modelApi';
import { DemoDockCreditSummary, DemoDockSkuId, demoDockSkus } from './creditExports';

export function getDemoDockSku(skuId: DemoDockSkuId) {
  return demoDockSkus.find((sku) => sku.id === skuId) ?? demoDockSkus[0];
}

function createDefaultSummary(): DemoDockCreditSummary {
  const sku = getDemoDockSku('free');
  return {
    account: {
      skuId: sku.id,
      creditsUsed: 0,
      reservedCredits: 0,
      cycleStartedAt: new Date().toISOString(),
    },
    sku,
    usedCredits: 0,
    reservedCredits: 0,
    remainingCredits: sku.monthlyCredits,
    totalCredits: sku.monthlyCredits,
    hasCredits: true,
  };
}

function createDefaultDemoDockCreditSummary(): DemoDockCreditSummary {
  const sku = getDemoDockSku('free');
  return {
    account: {
      skuId: sku.id,
      creditsUsed: 0,
      reservedCredits: 0,
      cycleStartedAt: ""
    },
    sku,
    usedCredits: 0,
    reservedCredits: 30,
    remainingCredits: 30,
    totalCredits: 30,
    hasCredits: true,
  }
};

export function useDemoDockCredits() {
  const [summary, setSummary] = useState<DemoDockCreditSummary>(createDefaultSummary);

  useEffect(() => {
    let active = true;

    async function syncAccount() {
      if (typeof fetch !== 'function') {
        return;
      }

      try {
        // const nextSummary = await getCreditAccount();
        const nextSummary = createDefaultDemoDockCreditSummary();
        if (active) {
          setSummary(nextSummary);
        }
      } catch {
        // Keep the default local view if the dev proxy is not running.
      }
    }

    syncAccount();
    window.addEventListener('demo-dock-credit-account-change', syncAccount);
    return () => {
      active = false;
      window.removeEventListener('demo-dock-credit-account-change', syncAccount);
    };
  }, []);

  const sku = useMemo(() => summary.sku, [summary.sku]);

  const setSku = useCallback(async (skuId: DemoDockSkuId) => {

    const nextSummary = await setCreditPlan(skuId);
    setSummary(nextSummary);
    window.dispatchEvent(new Event('demo-dock-credit-account-change'));
  }, []);

  const spendCredit = useCallback(async () => {
    try {
      const nextSummary = await spendDemoDockCredit();
      setSummary(nextSummary);
      window.dispatchEvent(new Event('demo-dock-credit-account-change'));
      return true;
    } catch {
      const latestSummary = await getCreditAccount().catch(() => null);
      if (latestSummary) {
        setSummary(latestSummary);
      }
      return false;
    }
  }, []);

  const resetCredits = useCallback(() => {
    window.dispatchEvent(new Event('demo-dock-credit-account-change'));
  }, []);

  return {
    account: summary.account,
    sku,
    usedCredits: summary.usedCredits,
    reservedCredits: summary.reservedCredits,
    remainingCredits: summary.remainingCredits,
    totalCredits: summary.totalCredits,
    hasCredits: summary.hasCredits,
    setSummary,
    setSku,
    spendCredit,
    resetCredits,
  };
}
